// Mission Control, first screen: everything an operator needs to decide
// whether to touch anything. The live numbers (KillSwitch, balance, spend,
// RunPod, queued, running) are in the HUD above; the tiles and panels here
// add what the HUD has no room for: the fleet, GPU time, storage, the
// balances, the last policy decisions and the task tail. Every panel
// re-reads on its own every OVERVIEW_REFRESH_MS.
//
// Every panel states its own source and its own failure. Compute being
// down must not make the users panel look empty, and "0 GPUs" must never
// be shown when the truth is "could not ask".
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  adminListUsers,
  adminStorage,
  adminStorageReconcile,
  type AdminUser,
  type StorageView,
  type TaskView,
} from "../../api/client";
import {
  fetchAnomalies,
  fetchComputeUnits,
  fetchDecisions,
  fetchRecentTasks,
  type Anomaly,
  type ComputeUnitRaw,
  type DecisionsEnvelope,
} from "../../api/admin";
import { ageLabel, ageSeconds, KILLSWITCH_STALE_S, type AdminStatus } from "../../lib/adminStatus";
import { describeRaw, gpuTime, liveGpuUnits, newestFirst, recentFailures, unitTone } from "../../lib/fleet";
import { formatMinor, formatSince, formatWhen } from "../../lib/format";
import { formatBytes } from "../../lib/results";

/** UserView carries no currency (internal/core/admin.go:13-56); the only
 * pricelist in V1 is USD cents, which is also what /account returns. */
const CURRENCY = "USD_CENTS";

export const OVERVIEW_REFRESH_MS = 15000;
const DAY_MS = 24 * 3_600_000;

/** Policy re-issues every lease on every tick; they would bury the
 * decisions an operator reads this panel for. */
const ROUTINE_DECISIONS = new Set(["LEASE"]);

export default function AdminOverview() {
  const status = useOutletContext<AdminStatus>();
  const [units, setUnits] = useState<ComputeUnitRaw[] | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [tasks, setTasks] = useState<TaskView[] | null>(null);
  const [decisions, setDecisions] = useState<DecisionsEnvelope | null>(null);
  const [storage, setStorage] = useState<StorageView | null>(null);
  const [anomalies, setAnomalies] = useState<Anomaly[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [readAt, setReadAt] = useState<number | null>(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileError, setReconcileError] = useState<string | null>(null);
  const inFlight = useRef(false);

  // Each source lands independently and keeps its own failure: a panel
  // whose fetch failed stays `null` and says "no answer from X", which is
  // a different statement from an empty list, and the only one that is
  // true. Collapsing both into [] would make Compute being down look like
  // a stopped fleet.
  const reload = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    const [u, us, t, d, st, an] = await Promise.allSettled([
      fetchComputeUnits(),
      adminListUsers(),
      fetchRecentTasks(15),
      fetchDecisions(),
      adminStorage(),
      fetchAnomalies(),
    ]);
    inFlight.current = false;
    setUnits(u.status === "fulfilled" ? u.value : null);
    setUsers(us.status === "fulfilled" ? us.value : null);
    setTasks(t.status === "fulfilled" ? t.value : null);
    setDecisions(d.status === "fulfilled" ? d.value : null);
    setStorage(st.status === "fulfilled" ? st.value : null);
    setAnomalies(an.status === "fulfilled" ? an.value : null);
    setReadAt(Date.now());
    setLoading(false);
  }, []);

  useEffect(() => {
    void reload();
    const timer = setInterval(() => void reload(), OVERVIEW_REFRESH_MS);
    return () => clearInterval(timer);
  }, [reload]);

  async function reconcileNow() {
    setReconciling(true);
    setReconcileError(null);
    try {
      await adminStorageReconcile();
      setStorage(await adminStorage());
    } catch (e) {
      setReconcileError(e instanceof Error ? e.message : "reconcile failed");
    } finally {
      setReconciling(false);
    }
  }

  const down: string[] = [];
  if (readAt !== null) {
    if (units === null) down.push("Compute (fleet)");
    if (users === null) down.push("Users (balances)");
    if (tasks === null) down.push("Tasks (activity)");
    if (decisions === null) down.push("Policy (decisions)");
    if (storage === null) down.push("Storage");
    if (anomalies === null) down.push("Compute (worker anomalies)");
  }

  const ks = status.killswitch;
  const ledger = status.summary?.ledger;
  const live = units ? liveGpuUnits(units) : null;
  const failedDay = units ? recentFailures(units, DAY_MS) : null;
  const day = units ? gpuTime(units, DAY_MS) : null;
  const week = units ? gpuTime(units, 7 * DAY_MS) : null;
  const atRunpod = ks?.live_resources?.runpod;
  const runpod = ks?.providers?.runpod;
  const runpodAge = ageSeconds(runpod?.read_at);
  const billed = ledger ? ledger.all.CHARGE - ledger.all.REFUND : null;
  const billedDay = ledger ? ledger.last_24h.CHARGE - ledger.last_24h.REFUND : null;
  const fleetRows = units ? fleetBoard(units) : null;
  const byBalance = users ? users.slice().sort((a, b) => b.balance_minor - a.balance_minor).slice(0, 8) : null;
  const shownDecisions = (decisions?.decisions ?? []).filter((d) => !ROUTINE_DECISIONS.has(d.decision ?? "")).slice(-8).reverse();

  return (
    <>
      <div className="a-head">
        <div>
          <h1 className="a-h1">Mission Control</h1>
          <p className="a-lede">
            Fleet, queue, spend, storage and the kill switch. Live readings sit in the bar above; this page is the detail
            behind them and re-reads every {OVERVIEW_REFRESH_MS / 1000} s.
          </p>
        </div>
        <div className="a-hud__cell--acts">
          <span className="a-panel__meta" data-testid="overview-read-at">
            {readAt ? `read ${new Date(readAt).toLocaleTimeString()}` : "reading…"}
          </span>
          <button className="a-btn" onClick={() => void reload()} disabled={loading}>
            {loading ? "Reading…" : "Reload panels"}
          </button>
        </div>
      </div>

      {down.length > 0 && (
        <p className="a-error">
          No answer from: {down.join(", ")}. Those panels are blank because they could not be read — not because they
          are empty.
        </p>
      )}

      <div className="a-tiles">
        <div className="a-tile">
          <span className="a-tile__label">
            <span className={"a-lamp " + (ks?.tripped ? "a-lamp--red" : ks ? "a-lamp--green" : "a-lamp--amber")} />
            KillSwitch
          </span>
          <span className={"a-tile__value " + (ks?.tripped ? "a-tile__value--red" : "a-tile__value--green")}>
            {ks ? (ks.tripped ? "TRIPPED" : "ARMED") : "···"}
          </span>
          <span className="a-tile__sub">{ks?.reason || "no trip condition recorded"}</span>
        </div>

        <div className="a-tile">
          <span className="a-tile__label">Short balance</span>
          <span className="a-tile__value a-tile__value--cyan">
            {ks?.balance_minor === undefined ? "···" : formatMinor(ks.balance_minor, CURRENCY)}
          </span>
          <span className="a-tile__sub">
            {(() => {
              const age = ageSeconds(ks?.inputs?.last_runpod_at);
              const stale = age === undefined || age > KILLSWITCH_STALE_S;
              return ks ? `${stale ? "stale · " : ""}${ageLabel(age)}` : "···";
            })()}
          </span>
        </div>

        <div className="a-tile" data-testid="tile-runpod">
          <span className="a-tile__label">RunPod</span>
          <span className="a-tile__value">{runpod ? `${formatMinor(runpod.spend_per_hr_minor, CURRENCY)}/h` : "···"}</span>
          <span className="a-tile__sub">
            {runpod
              ? `balance ${formatMinor(runpod.balance_minor, CURRENCY)} · spent ${formatMinor(ks?.inputs?.runpod_spent_total_minor ?? 0, CURRENCY)} · ${ageLabel(runpodAge)}`
              : "no reading from KillSwitch yet"}
          </span>
        </div>

        <div className="a-tile" data-testid="tile-live-gpus">
          <span className="a-tile__label">Live GPUs</span>
          <span className={"a-tile__value " + (live && live.length > 0 ? "a-tile__value--amber" : "")}>
            {live ? live.length : "···"}
          </span>
          <span className="a-tile__sub">
            {atRunpod === undefined || atRunpod === null ? "RunPod count pending" : `${atRunpod} live at RunPod`}
          </span>
        </div>

        <div className="a-tile" data-testid="tile-failed">
          <span className="a-tile__label">
            {failedDay && failedDay.length > 0 && <span className="a-lamp a-lamp--red" />}
            Failed units, 24 h
          </span>
          <span className={"a-tile__value " + (failedDay && failedDay.length > 0 ? "a-tile__value--red" : "")}>
            {failedDay ? failedDay.length : "···"}
          </span>
          <span className="a-tile__sub">
            {failedDay && failedDay.length > 0 ? (
              <Link to="/admin/compute">needs attention →</Link>
            ) : (
              "none"
            )}
          </span>
        </div>

        <div className="a-tile" data-testid="tile-gpu-time">
          <span className="a-tile__label">GPU time, 24 h</span>
          <span className="a-tile__value">{day ? `${day.hours.toFixed(1)} h` : "···"}</span>
          <span className="a-tile__sub">
            {day && week
              ? `~${formatMinor(day.listCostMinor, CURRENCY)} at list · 7 d ${week.hours.toFixed(1)} h, ~${formatMinor(week.listCostMinor, CURRENCY)}`
              : "compute unreachable"}
          </span>
        </div>

        <div className="a-tile" data-testid="tile-billed">
          <span className="a-tile__label">Billed to users</span>
          <span className="a-tile__value">{billed === null ? "···" : formatMinor(billed, CURRENCY)}</span>
          <span className="a-tile__sub">
            {billedDay === null || !ledger
              ? "core unreachable"
              : `24 h ${formatMinor(billedDay, CURRENCY)} · refunds ${formatMinor(ledger.all.REFUND, CURRENCY)}`}
          </span>
        </div>

        <div className="a-tile" title="claim to finish × the unit's list price; boot, idle and warm time are not in it">
          <span className="a-tile__label">Metered job cost</span>
          <span className="a-tile__value">{ledger ? formatMinor(ledger.all.COST, CURRENCY) : "···"}</span>
          <span className="a-tile__sub">
            {ledger ? `24 h ${formatMinor(ledger.last_24h.COST, CURRENCY)} · not provider spend` : "core unreachable"}
          </span>
        </div>

        <div className="a-tile">
          <span className="a-tile__label">Accounts</span>
          <span className="a-tile__value">{users ? users.length : "···"}</span>
          <span className="a-tile__sub">
            {users ? `${users.filter((u) => !u.disabled_at).length} active · ${users.filter((u) => u.disabled_at).length} disabled` : "users unreachable"}
          </span>
        </div>
      </div>

      <div className="a-grid a-grid--12">
        <section className="a-panel">
          <div className="a-panel__head">
            <h2 className="a-panel__title">GPU fleet</h2>
            <Link className="a-panel__meta" to="/admin/compute">
              All units →
            </Link>
          </div>
          {fleetRows === null ? (
            <p className="a-empty">no answer from compute</p>
          ) : fleetRows.length === 0 ? (
            <p className="a-empty">no units yet</p>
          ) : (
            <>
              {live !== null && live.length === 0 && <p className="a-note">Nothing is rented now. The latest units:</p>}
              <table className="a-table" data-testid="fleet-table">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>State</th>
                    <th>Runtime</th>
                    <th>Hardware</th>
                    <th>Created</th>
                    <th>For</th>
                  </tr>
                </thead>
                <tbody>
                  {fleetRows.map((u, i) => (
                    <tr key={u.ID ?? i}>
                      <td className="a-table__hi">{u.ID?.slice(0, 12) ?? "—"}</td>
                      <td>
                        <span className="a-hud__state">
                          <span
                            className={
                              "a-lamp " +
                              (unitTone(u.State) === "ok"
                                ? "a-lamp--green"
                                : unitTone(u.State) === "busy"
                                  ? "a-lamp--amber"
                                  : unitTone(u.State) === "bad"
                                    ? "a-lamp--red"
                                    : "")
                            }
                          />
                          <span className="a-state">{u.State ?? "UNKNOWN"}</span>
                        </span>
                      </td>
                      <td className="a-table__dim">{u.Runtime ?? "—"}</td>
                      <td className="a-table__dim">{offeringLabel(u)}</td>
                      <td className="a-table__dim">{formatWhen(u.CreatedAt)}</td>
                      <td className="a-table__dim">{lifetime(u)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>

        <div className="a-grid">
          <section className="a-panel">
            <div className="a-panel__head">
              <h2 className="a-panel__title">Last policy decisions</h2>
              <span className="a-panel__meta">
                {decisions ? `${decisions.paused ? "loop paused" : "loop running"} · since Policy started · leases hidden` : "unreachable"}
              </span>
            </div>
            {decisions === null ? (
              <p className="a-empty">no answer from policy</p>
            ) : shownDecisions.length === 0 ? (
              <p className="a-empty">no decisions since Policy started</p>
            ) : (
              <table className="a-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Decision</th>
                    <th>Runtime</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {shownDecisions.map((d, i) => (
                    <tr key={`${d.job_id ?? i}-${i}`}>
                      <td className="a-table__dim">{formatSince(d.as_of)}</td>
                      <td className="a-table__hi">{d.decision ?? "—"}</td>
                      <td className="a-table__dim">{d.runtime ?? "—"}</td>
                      <td className="a-table__dim">{d.reason ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="a-panel">
            <div className="a-panel__head">
              <h2 className="a-panel__title">Balances</h2>
              <Link className="a-panel__meta" to="/admin/users">
                Manage →
              </Link>
            </div>
            {byBalance === null ? (
              <p className="a-empty">no answer from core</p>
            ) : byBalance.length === 0 ? (
              <p className="a-empty">no accounts</p>
            ) : (
              <table className="a-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Role</th>
                    <th className="a-table__num">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {byBalance.map((u) => (
                    <tr key={u.id}>
                      <td className="a-table__hi">{u.email}</td>
                      <td className="a-table__dim">{u.role}</td>
                      <td className="a-table__num a-table__hi">{formatMinor(u.balance_minor, CURRENCY)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      </div>

      <AnomalyPanel anomalies={anomalies} />

      <StoragePanel storage={storage} reconciling={reconciling} error={reconcileError} onReconcile={() => void reconcileNow()} />

      <section className="a-panel">
        <div className="a-panel__head">
          <h2 className="a-panel__title">Task activity</h2>
          <Link className="a-panel__meta" to="/admin/tasks">
            All tasks →
          </Link>
        </div>
        {tasks === null ? (
          <p className="a-empty">no answer from core</p>
        ) : tasks.length === 0 ? (
          <p className="a-empty">no tasks yet</p>
        ) : (
          <div className="a-scroll">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Alias</th>
                  <th>State</th>
                  <th>Class</th>
                  <th className="a-table__num">Done</th>
                  <th className="a-table__num">Failed</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.task_id}>
                    <td>
                      <Link to={`/admin/tasks/${t.task_id}`}>{t.task_id.slice(0, 8)}</Link>
                    </td>
                    <td className="a-table__dim">{t.alias}</td>
                    <td>
                      <span className={`a-state a-state--${t.state.toLowerCase()}`}>{t.state}</span>
                    </td>
                    <td className="a-table__dim">{t.scheduling_class}</td>
                    <td className="a-table__num">
                      {t.done} / {t.requested}
                    </td>
                    <td className={`a-table__num ${t.failed > 0 ? "a-table__hi" : "a-table__dim"}`}>{t.failed}</td>
                    <td className="a-table__dim">{formatWhen(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

/** Rule E's judgements of the last 24 h (ADR-0064): a worker far off its
 * peers while fetching, loading or generating, and whether Policy kept it
 * (the result still pays) or stopped it before it cost more. This list is
 * the notification; nothing is pushed out of the system yet. */
function AnomalyPanel({ anomalies }: { anomalies: Anomaly[] | null }) {
  const stops = anomalies?.filter((a) => a.verdict === "STOP").length ?? 0;
  return (
    <section className="a-panel" data-testid="anomaly-panel">
      <div className="a-panel__head">
        <h2 className="a-panel__title">
          {stops > 0 && <span className="a-lamp a-lamp--red" />}
          Worker anomalies, 24 h
        </h2>
        <span className="a-panel__meta">
          {anomalies ? `${anomalies.length} judged · ${stops} stopped · live estimate (Rule E)` : "unreachable"}
        </span>
      </div>
      {anomalies === null ? (
        <p className="a-empty">no answer from compute</p>
      ) : anomalies.length === 0 ? (
        <p className="a-empty">every worker kept pace with its peers</p>
      ) : (
        <div className="a-scroll">
          <table className="a-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Unit</th>
                <th>Deployment</th>
                <th>Stage</th>
                <th>Verdict</th>
                <th className="a-table__num">To result</th>
                <th className="a-table__num">Finish / replace / worth</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {anomalies.slice(0, 20).map((a) => (
                <tr key={a.id}>
                  <td className="a-table__dim">{formatSince(a.at)}</td>
                  <td className="a-table__hi">{a.compute_unit_id.slice(0, 12)}</td>
                  <td className="a-table__dim">{a.deployment}</td>
                  <td>{a.stage}</td>
                  <td>
                    <span className={`a-state a-state--${a.verdict === "STOP" ? "failed" : a.verdict === "KEEP" ? "running" : "queued"}`}>
                      {a.verdict}
                    </span>
                  </td>
                  <td className="a-table__num">{a.eta_s === undefined ? "—" : `${Math.round(a.eta_s)} s`}</td>
                  <td className="a-table__num a-table__dim">
                    {[a.cost_keep_minor, a.cost_replace_minor, a.value_minor]
                      .map((v) => (v === undefined ? "—" : formatMinor(Math.round(v), CURRENCY)))
                      .join(" / ")}
                  </td>
                  <td className="a-table__dim">{a.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The fleet board's rows: every live GPU unit, then the newest others up
 * to eight rows, so an empty fleet still shows what ran last. */
function fleetBoard(units: ComputeUnitRaw[]): ComputeUnitRaw[] {
  const live = liveGpuUnits(units);
  const liveIds = new Set(live.map((u) => u.ID));
  const rest = newestFirst(units).filter((u) => !liveIds.has(u.ID));
  return [...live, ...rest].slice(0, Math.max(8, live.length));
}

/** "RTX 6000 Ada · 99 c/h" from the unit's offering and list price. */
function offeringLabel(u: ComputeUnitRaw): string {
  const offering = (u.Offering ?? "").split(":").slice(2).join(":") || describeRaw(u.Hardware);
  const p = (u.Pricing as { price_per_hour_minor?: number } | null)?.price_per_hour_minor;
  return typeof p === "number" && p > 0 ? `${offering} · ${formatMinor(p, CURRENCY)}/h` : offering;
}

/** How long the unit lived (or has lived so far). */
function lifetime(u: ComputeUnitRaw): string {
  const end = u.StoppedAt ?? u.FailedAt;
  if (!end) return formatSince(u.CreatedAt);
  const ms = Date.parse(end) - Date.parse(u.CreatedAt ?? "");
  return Number.isNaN(ms) ? "—" : formatSince(new Date(Date.now() - ms).toISOString());
}

function StoragePanel({
  storage,
  reconciling,
  error,
  onReconcile,
}: {
  storage: StorageView | null;
  reconciling: boolean;
  error: string | null;
  onReconcile: () => void;
}) {
  const committed = storage?.by_kind.filter((k) => k.state === "COMMITTED") ?? [];
  const keptCount = committed.reduce((n, k) => n + k.count, 0);
  const keptBytes = committed.reduce((n, k) => n + k.bytes, 0);
  const pending = storage?.by_kind.filter((k) => k.state === "ALLOCATED").reduce((n, k) => n + k.count, 0) ?? 0;
  const deleted = storage?.by_kind.filter((k) => k.state === "DELETED").reduce((n, k) => n + k.count, 0) ?? 0;
  const rep = storage?.reconcile ?? null;

  return (
    <section className="a-panel" data-testid="storage-panel">
      <div className="a-panel__head">
        <h2 className="a-panel__title">Storage</h2>
        <span className="a-hud__cell--acts">
          <span className="a-panel__meta">
            {rep ? `bucket checked ${formatSince(rep.finished_at)} ago` : "bucket not checked since Core started"}
          </span>
          <button className="a-btn" onClick={onReconcile} disabled={reconciling || storage === null}>
            {reconciling ? "Checking bucket…" : "Check bucket now"}
          </button>
        </span>
      </div>
      {error && <p className="a-error">{error}</p>}
      {storage === null ? (
        <p className="a-empty">no answer from core</p>
      ) : (
        <>
          <div className="a-tiles">
            <div className="a-tile">
              <span className="a-tile__label">Kept artifacts</span>
              <span className="a-tile__value" data-testid="storage-kept">
                {formatBytes(keptBytes)}
              </span>
              <span className="a-tile__sub">
                {keptCount} committed · {pending} uploading · {deleted} deleted (rows kept {Math.round((storage.config.intermediate_grace_s ?? 0) / 86400)} d)
              </span>
            </div>
            <div className="a-tile">
              <span className="a-tile__label">In the bucket</span>
              <span className="a-tile__value" data-testid="storage-bucket">
                {rep ? formatBytes(rep.bytes) : "···"}
              </span>
              <span className="a-tile__sub">{rep ? `${rep.objects} objects · ${rep.tombstones} tombstones` : "after the first check"}</span>
            </div>
            <div className="a-tile">
              <span className="a-tile__label">
                {rep && (rep.missing_bytes > 0 || rep.orphans_failed > 0 || rep.error) && <span className="a-lamp a-lamp--red" />}
                Last check
              </span>
              <span className={"a-tile__value " + (rep && (rep.missing_bytes > 0 || rep.error) ? "a-tile__value--red" : "")}>
                {rep ? (rep.error ? "failed" : rep.missing_bytes > 0 ? `${rep.missing_bytes} missing` : "consistent") : "···"}
              </span>
              <span className="a-tile__sub" title={rep?.missing_ids?.join(", ")}>
                {rep
                  ? rep.error
                    ? rep.error
                    : rep.dry_run
                      ? `dry run: ${rep.orphans_found} orphans would go (${formatBytes(rep.orphan_bytes)})`
                      : `${rep.orphans_removed} orphans removed (${formatBytes(rep.orphan_bytes)})${rep.orphans_failed ? ` · ${rep.orphans_failed} failed` : ""}${rep.orphans_young ? ` · ${rep.orphans_young} unowned under 1 h, kept` : ""}`
                  : "—"}
              </span>
            </div>
          </div>

          <div className="a-grid a-grid--12">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>State</th>
                  <th className="a-table__num">Count</th>
                  <th className="a-table__num">Size</th>
                </tr>
              </thead>
              <tbody>
                {storage.by_kind.map((k) => (
                  <tr key={`${k.kind}-${k.state}`}>
                    <td className="a-table__hi">{k.kind}</td>
                    <td className="a-table__dim">{k.state}</td>
                    <td className="a-table__num">{k.count}</td>
                    <td className="a-table__num">{formatBytes(k.bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="a-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th className="a-table__num">Kept</th>
                  <th className="a-table__num">Size</th>
                </tr>
              </thead>
              <tbody>
                {storage.by_user.slice(0, 8).map((u) => (
                  <tr key={u.user_id}>
                    <td className="a-table__hi">{u.email || u.user_id.slice(0, 8)}</td>
                    <td className="a-table__num">{u.count}</td>
                    <td className="a-table__num">{formatBytes(u.bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
